import { getSprites, gidTransform, tileId, tileSource, type TiledMap } from '@veriverse/map-format';
import { TILE_SIZE } from '@veriverse/shared';
import type Phaser from 'phaser';
import { spriteDepth } from './depth.js';

/**
 * Draws the map's sprite objects (ADR-0022): one container per sprite, built from tileset frames
 * and depth-sorted against characters by the sprite's bottom row. Tiles whose tileset image did
 * not load are left out, like on tile layers.
 */
export function addSpriteObjects(
  scene: Phaser.Scene,
  map: TiledMap,
  textureKey: (image: string) => string,
): Phaser.GameObjects.Container[] {
  const containers: Phaser.GameObjects.Container[] = [];
  for (const sprite of getSprites(map)) {
    if (!sprite.visible) continue;
    const images: Phaser.GameObjects.Image[] = [];
    sprite.rows.forEach((row, dy) =>
      row.forEach((gid, dx) => {
        const source = tileSource(map, gid);
        if (!source) return;
        const key = textureKey(source.tileset.image);
        if (!scene.textures.exists(key)) return;
        const texture = scene.textures.get(key);
        const frame = `tile:${tileId(gid)}`;
        if (!texture.has(frame)) texture.add(frame, 0, source.sx, source.sy, TILE_SIZE, TILE_SIZE);
        // Tiled's flip bits (including the diagonal one used for quarter turns).
        const { angle, flipX, flipY } = gidTransform(gid);
        images.push(
          scene.add
            .image((dx + 0.5) * TILE_SIZE, (dy + 0.5) * TILE_SIZE, key, frame)
            .setFlip(flipX, flipY)
            .setAngle(angle),
        );
      }),
    );
    containers.push(
      scene.add
        .container(sprite.x * TILE_SIZE, sprite.y * TILE_SIZE, images)
        .setDepth(spriteDepth(sprite.sortRow)),
    );
  }
  return containers;
}
